import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import type { ErrorPolicy } from 'apollo-client';

import { useCreateSpaceExperiment } from '@confluence/create-space/entry-points/createSpace';
import { useIsLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled';
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { useExperimentalPromotedSpacesQuery } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalPromotedSpacesQuery';

import { SpaceEnabledContentTypesQuery } from '../queries/SpaceEnabledContentTypesQuery.graphql';
import type {
	SpaceEnabledContentTypesQuery as SpaceEnabledContentTypesQueryType,
	SpaceEnabledContentTypesQueryVariables,
} from '../queries/__types__/SpaceEnabledContentTypesQuery';
// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
import { GetDefaultSpaceQuery } from '../queries/GetDefaultSpaceQuery.experimentalgraphql';
import type {
	GetDefaultSpaceQuery as GetDefaultSpaceQueryType,
	GetDefaultSpaceQueryVariables,
} from '../queries/__types__/GetDefaultSpaceQuery';

export const useSpaceEnabledContentTypes = (spaceKey: string | undefined) => {
	const { isSpaceTypesCreationFlowEnabled } = useCreateSpaceExperiment();
	const { isLivePagesFeatureEnabled } = useIsLivePagesFeatureEnabled();

	const isFeatureWhichRequiresEditFeaturesEnabled =
		isSpaceTypesCreationFlowEnabled || isLivePagesFeatureEnabled;

	const defaultSpaceQueryArgs = {
		skip: !isFeatureWhichRequiresEditFeaturesEnabled || spaceKey !== undefined,
		variables: {
			promotedSpacesLimit: 1,
			otherSpacesLimit: 1,
			promotedSpaceKey: null,
		},
	};
	const { data: defaultSpaceKeyData } = expValEquals(
		'confluence_deprecate_experimental_schema_exp_2',
		'isEnabled',
		true,
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useExperimentalPromotedSpacesQuery(defaultSpaceQueryArgs)
		: // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useQuery<GetDefaultSpaceQueryType, GetDefaultSpaceQueryVariables>(
				// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Experimental Schema GraphQL Operation
				GetDefaultSpaceQuery,
				defaultSpaceQueryArgs,
			);

	const spaceEnabledContentTypesQueryOptions = {
		errorPolicy: 'all' as ErrorPolicy,
		skip: !isFeatureWhichRequiresEditFeaturesEnabled || (!spaceKey && !defaultSpaceKeyData),
		variables: {
			spaceKey: spaceKey || defaultSpaceKeyData?.experimentalPromotedSpaces?.nodes?.[0]?.id,
		},
	};
	const { data: enabledContentTypesData } = fg('stop_watching_space_level_query')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useQueryNoFurtherUpdateInterop<
				SpaceEnabledContentTypesQueryType,
				SpaceEnabledContentTypesQueryVariables
			>(
				// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
				SpaceEnabledContentTypesQuery,
				spaceEnabledContentTypesQueryOptions,
			)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useQuery<SpaceEnabledContentTypesQueryType, SpaceEnabledContentTypesQueryVariables>(
				// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
				SpaceEnabledContentTypesQuery,
				spaceEnabledContentTypesQueryOptions,
			);

	const defaultReturnValue = !isSpaceTypesCreationFlowEnabled;

	const isCreateWhiteboardEnabled = enabledContentTypesData
		? enabledContentTypesData?.space?.spaceTypeSettings?.enabledContentTypes?.isWhiteboardsEnabled
		: defaultReturnValue;

	const isCreateBlogEnabled = enabledContentTypesData
		? enabledContentTypesData?.space?.spaceTypeSettings?.enabledContentTypes?.isBlogsEnabled
		: defaultReturnValue;

	const isCreateDatabaseEnabled = enabledContentTypesData
		? enabledContentTypesData?.space?.spaceTypeSettings?.enabledContentTypes?.isDatabasesEnabled
		: defaultReturnValue;

	const isCreateSmartLinkEnabled = enabledContentTypesData
		? enabledContentTypesData?.space?.spaceTypeSettings?.enabledContentTypes?.isEmbedsEnabled
		: defaultReturnValue;

	const isCreateLiveDocEnabled = enabledContentTypesData
		? enabledContentTypesData?.space?.spaceTypeSettings?.enabledContentTypes?.isLivePagesEnabled
		: false;

	const isCreateFolderEnabled = enabledContentTypesData
		? enabledContentTypesData?.space?.spaceTypeSettings?.enabledContentTypes?.isFoldersEnabled
		: defaultReturnValue;

	return {
		isCreateWhiteboardEnabled,
		isCreateBlogEnabled,
		isCreateDatabaseEnabled,
		isCreateSmartLinkEnabled,
		isCreateLiveDocEnabled,
		isCreateFolderEnabled,
	};
};
